// Fronted/src/contexts/paymentContext.js
import { createContext, useContext, useState } from 'react';
import PaymentService from '../services/paymentService';

const PaymentContext = createContext();

export const usePayment = () => {
  return useContext(PaymentContext);
};

export const PaymentProvider = ({ children }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const initializePayment = async (paymentData) => {
    setIsProcessing(true);
    setPaymentError(null);
    setPaymentSuccess(false);
    setPaymentId(null);

    try {
      
      const response = await PaymentService.initiatePayment(paymentData);
      const data = response;

      if (data.success) {
        setPaymentId(data.data.payment_id);
        return {
          success: true,
          payment_url: data.data.payment_url,
          payment_data: data.data.payment_data,
        };
      } else {
        setPaymentError(data.message || 'Payment initialization failed.');
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error initializing payment:', error);
      setPaymentError('Payment initialization failed.');
      return { success: false, error: 'Network error' };
    } finally {
      setIsProcessing(false);
    }
  };

  const checkPaymentStatus = async (id) => {
    try {
      const response = await PaymentService.getPaymentStatus(id);
      const data = response;

      if (data.success) {
        if (data.data.status === 'completed') {
          setPaymentSuccess(true);
        }
        return { success: true, status: data.data.status };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const getAllPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await PaymentService.getAllPayments();
      setOfferings(response.data);
    } catch (error) {
      console.error('Error fetching all payments:', error);
      setError(error.message || 'Failed to fetch payments');
      return { success: false, error: 'Network error' };
    }
    finally {
      setLoading(false);
    }
  }

  const resetPayment = () => {
    setPaymentSuccess(false);
    setPaymentError(null);
    setPaymentId(null);
  };

  // Helper function to redirect to PayFast with the correct data
  const redirectToPayFast = (paymentDetails) => {
    const { payment_url, payment_data } = paymentDetails;
    // 1. Create the form HTML string
    let htmlForm = `<form id="payfast-redirect-form" action="${payment_url}" method="post">`;
    
    // 2. Add all hidden inputs
    Object.keys(payment_data).forEach(key => {
      const value = payment_data[key];
      if (value !== null && value !== undefined && value !== '') {
        htmlForm += `<input name="${key}" type="hidden" value="${value.toString().trim()}" />`;
      }
    });
    
    // 3. Close the form
    htmlForm += '</form>';
    
    // 4. Create a temporary div to hold our form
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlForm;
    
    // 5. Get the form element
    const form = tempDiv.querySelector('form');
    
    // 6. Append to body and submit
    document.body.appendChild(form);
    form.submit();
    
    // 7. Clean up after submission
    setTimeout(() => {
      if (document.body.contains(form)) {
        document.body.removeChild(form);
      }
    }, 1000);
  };

  const value = {
    isProcessing,
    paymentError,
    paymentSuccess,
    paymentId,
    initializePayment,
    checkPaymentStatus,
    resetPayment,
    redirectToPayFast,
    offerings,
    getAllPayments,
    loading,
    error
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentContext;